import React, {
    useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import useAppChrome from '../../hooks/useAppChrome';
import {
    Button, EmptyState, ErrorState, LoadingState, ToastRegion, useToast,
} from '../ui';
import { buildMachineCatalog } from '../../utils/machineFilters';
import {
    backLabel, contextViewName, stepperPosition, tabFromSearch, unitPath,
} from '../../utils/unitRoute';
import scrollInstant from '../../utils/scrollInstant';
import { committedUnitView } from '../../utils/committedUnit';
import UnitPageV2 from './UnitPageV2';
import { BackIcon } from './icons';
import '../ui/ui.css';
import '../InventoryV2/InventoryV2.css';
import './UnitV2.css';

const EDITABLE = /^(INPUT|TEXTAREA|SELECT)$/;
const isEditable = (el) => Boolean(el && (EDITABLE.test(el.tagName) || el.isContentEditable));
// Something modal is open (a dialog, the photo viewer): it owns the keyboard.
const modalOpen = () => Boolean(document.querySelector('[aria-modal="true"]'));

/**
 * /unit/:stock — the unit detail as a full page (U1), with the new design on.
 *
 * - Cold load (P-UNIT-026): the inventory loads first (there is no single-unit
 *   endpoint). A stock number that is not in it may be a committed unit
 *   (sale pending, pre-sold, sold; decisions/inventory-columns.md): the
 *   committed units are then loaded once (DataContext.loadCommitted, kept for
 *   the session) and the unit opens from there, shown without its
 *   in-stock-only figures (utils/committedUnit). Only a stock number in
 *   neither list shows the not-found state.
 * - Back (D7): returns to the page the user came from (history state `from`):
 *   the inventory (its filters, sort, page and scroll come back), Tasks or
 *   Branches. Without one (a shared link), Back goes to the inventory.
 * - Escape (D6): the innermost layer first — popovers, dialogs, the photo
 *   viewer and inline forms stop it themselves; a focused field is left
 *   (blurred) — and only then back. No click-outside: a page has no outside.
 * - Prev / next and J / K (U2) walk the list of the view the user came from;
 *   the filter pills say which view that is. Opened without one (a link, a
 *   reload of a link), the stepper walks the whole inventory.
 * - The tab is in the address (?tab=, replaced on change, never a new
 *   history step); the page's h1 takes focus when a unit opens.
 */
export default function UnitRouteV2() {
    const { stock: rawStock } = useParams();
    const stock = String(rawStock ?? '');
    const location = useLocation();
    const navigate = useNavigate();
    const data = useData();
    const { readOnly } = useAppChrome({ data });
    const { toast, showToast, dismissToast } = useToast();
    const titleRef = useRef(null);
    const barRef = useRef(null);

    const navState = useMemo(() => location.state || {}, [location.state]);
    const from = navState.from || null;
    const tab = tabFromSearch(location.search);

    useEffect(() => { data.loadInventory?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const allUnits = useMemo(() => data.inventoryData?.units || [], [data.inventoryData]);
    const machineCatalog = useMemo(() => buildMachineCatalog(allUnits), [allUnits]);
    const regularUnit = useMemo(
        () => allUnits.find((u) => String(u.StockNumber) === stock) || null,
        [allUnits, stock],
    );
    // Not in the inventory: maybe a committed unit (loaded on demand).
    const { committedData, committedError, loadCommitted } = data;
    const committedUnit = useMemo(
        () => (regularUnit ? null : (committedData?.units || []).find((u) => String(u.StockNumber) === stock) || null),
        [regularUnit, committedData, stock],
    );
    const committed = Boolean(committedUnit);
    const unit = useMemo(
        () => regularUnit || (committedUnit ? committedUnitView(committedUnit) : null),
        [regularUnit, committedUnit],
    );
    const needCommitted = Boolean(data.inventoryData) && !regularUnit && !committedData && typeof loadCommitted === 'function';
    useEffect(() => {
        if (needCommitted && !committedError) loadCommitted();
    }, [needCommitted]); // eslint-disable-line react-hooks/exhaustive-deps

    // The list prev / next walks: the view the user came from, or the whole
    // inventory in its default order.
    const stocks = useMemo(
        () => (Array.isArray(navState.stocks) ? navState.stocks : allUnits.map((u) => String(u.StockNumber))),
        [navState.stocks, allUnits],
    );
    const pos = stepperPosition(stocks, stock);
    const pills = Array.isArray(navState.pills) ? navState.pills : null;

    // Arrived via "Create task": open the create form once, then forget it,
    // so a reload or coming back does not pop it up again.
    const [createOnOpen] = useState(Boolean(navState.createTask));
    useEffect(() => {
        if (!navState.createTask) return;
        navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: { ...navState, createTask: false } });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const changeTab = useCallback((id) => {
        navigate({ pathname: location.pathname, search: `?tab=${id}` }, { replace: true, state: location.state });
    }, [navigate, location.pathname, location.state]);

    const goToUnit = useCallback((next) => {
        if (!next) return;
        navigate(unitPath(next, tab), { replace: true, state: { ...navState, createTask: false } });
    }, [navigate, tab, navState]);

    const goBack = useCallback(() => {
        // Came from a page of the app: step back to its history entry, which
        // restores its address (and the inventory its view). Otherwise there
        // is no entry to return to.
        if (from) navigate(-1);
        else navigate('/', { replace: true });
    }, [from, navigate]);

    // Escape (D6) and J / K (U2).
    const keys = useRef({});
    keys.current = { goBack, goToUnit, prev: pos.prev, next: pos.next };
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.defaultPrevented) return;
            const k = keys.current;
            if (e.key === 'Escape') {
                if (modalOpen()) return;
                if (isEditable(e.target)) { e.target.blur(); return; }
                k.goBack();
                return;
            }
            if (e.metaKey || e.ctrlKey || e.altKey || isEditable(e.target) || modalOpen()) return;
            if (e.key === 'j' || e.key === 'J') k.goToUnit(k.next);
            else if (e.key === 'k' || e.key === 'K') k.goToUnit(k.prev);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // A unit opens with its title focused (route change, prev / next), so a
    // screen reader starts at the unit, unless the user is working the
    // stepper (focus stays on the button they pressed).
    useLayoutEffect(() => {
        if (!unit) return;
        const active = document.activeElement;
        if (active && barRef.current && barRef.current.contains(active) && active !== document.body) return;
        // At the top, instantly: <html> scrolls smoothly, and a smooth scroll
        // is cut short by the next render (QA BUG-1).
        scrollInstant(0);
        titleRef.current?.focus({ preventScroll: true });
    }, [unit?.StockNumber]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (unit) document.title = `#${unit.StockNumber} ${unit.MAKE || ''} ${unit.MODEL || ''} · IronSight`.replace(/\s+/g, ' ');
    }, [unit]);
    // Leaving the page gives the tab its title back (QA BUG-5). Read at the
    // first render, before the title above is set.
    const titleBefore = useRef(null);
    if (titleBefore.current === null) titleBefore.current = document.title;
    useEffect(() => () => { document.title = titleBefore.current; }, []);

    const loading = !data.inventoryData && !data.inventoryError;
    let body;
    if (loading) {
        body = <LoadingState label="Loading inventory data..." />;
    } else if (!unit && data.inventoryError) {
        body = <ErrorState message={data.inventoryError} onRetry={() => data.loadInventory?.(true)} />;
    } else if (needCommitted && committedError) {
        body = <ErrorState message={committedError} onRetry={() => loadCommitted()} />;
    } else if (needCommitted) {
        body = <LoadingState label="Loading unit..." />;
    } else if (!unit) {
        body = (
            <EmptyState
                title="Unit not found"
                message={`Stock #${stock} is not in this dealership's current inventory or its committed units. It may have been closed out or removed.`}
                action={<Button variant="primary" onClick={() => navigate('/', { replace: !from })}>Go to inventory</Button>}
            />
        );
    } else {
        body = (
            <UnitPageV2
                key={unit.StockNumber}
                unit={unit}
                committed={committed}
                tab={tab}
                onTabChange={changeTab}
                createOnOpen={createOnOpen}
                allUnits={allUnits}
                machineCatalog={machineCatalog}
                readOnly={readOnly}
                showToast={showToast}
                titleRef={titleRef}
            />
        );
    }

    return (
        <div className="u2" data-testid="unit-v2">
            <main className="u2-page">
                <div className="u2-ctxbar" ref={barRef}>
                    <button
                        type="button"
                        className="u2-back"
                        onClick={goBack}
                        title={from?.kind === 'inventory' || !from
                            ? 'Returns to the same view, filters, sort and scroll position'
                            : undefined}
                    >
                        <BackIcon />
                        {backLabel(from)}
                    </button>
                    {pills && (
                        <span className="u2-ctx" aria-label="Inventory filters you came from">
                            from <b>{contextViewName(pills)}</b>
                            {pills.map((p) => (
                                <span key={`${p.label}:${p.value}`} className="u2-ctx-pill">
                                    <span>{p.label}:</span> {p.value}
                                </span>
                            ))}
                        </span>
                    )}
                    {/* Only when there is a list to walk: from Tasks or Branches
                        there is one unit, and a dead "1 of 1" helps no one. */}
                    {unit && pos.index !== -1 && pos.total > 1 && (
                        <span className="u2-stepper" aria-label="Move through units in this view">
                            <button
                                type="button"
                                className="u2-ib"
                                onClick={() => goToUnit(pos.prev)}
                                disabled={!pos.prev}
                                aria-label="Previous unit in this view"
                                title="Previous unit (K)"
                            >
                                ‹
                            </button>
                            <span className="u2-num">
                                {(pos.index + 1).toLocaleString('en-US')} of {pos.total.toLocaleString('en-US')}
                            </span>
                            <button
                                type="button"
                                className="u2-ib"
                                onClick={() => goToUnit(pos.next)}
                                disabled={!pos.next}
                                aria-label="Next unit in this view"
                                title="Next unit (J)"
                            >
                                ›
                            </button>
                            <span className="u2-kbd" aria-hidden="true">J</span>
                            <span className="u2-kbd" aria-hidden="true">K</span>
                        </span>
                    )}
                </div>
                {body}
            </main>
            <footer className="u2-appfoot">
                <span>&copy; 2026 Fusable. All rights reserved.</span>
            </footer>
            <ToastRegion toast={toast} onDismiss={dismissToast} />
        </div>
    );
}
